import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  HStack,
  Icon,
  Spacer,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import {
  ArrowPathIcon,
  BoltIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CloudArrowDownIcon,
  ExclamationTriangleIcon,
  KeyIcon,
} from '@heroicons/react/24/outline';
import { NavLink, useParams } from 'react-router-dom';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, formatDistanceToNow } from 'date-fns';
import WorkspaceLayout from '@/pages/workspace/workspace-layout';
import useIsInAdminGroup from '@/lib/hooks/use-is-in-admin-group';
import api from '@/utils/api';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Filler);

type Range = '1h' | '24h' | '7d' | '30d';

const RANGES: { id: Range; label: string }[] = [
  { id: '1h', label: 'Last hour' },
  { id: '24h', label: '24h' },
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
];

interface Bucket {
  bucket: string;
  total: number;
  errors: number;
  p95_ms: number;
}

interface EndpointStat {
  method: string;
  path: string;
  total: number;
  errors: number;
  p95_ms: number;
}

interface KeyStat {
  api_key_id: number;
  name: string;
  total: number;
  errors: number;
  last_seen: string;
}

interface Summary {
  range_start: string;
  range_end: string;
  bucket: 'hour' | 'day';
  total: number;
  success: number;
  errors_4xx: number;
  errors_5xx: number;
  p50_ms: number;
  p95_ms: number;
  p99_ms: number;
  timeseries: Bucket[];
  by_endpoint: EndpointStat[];
  by_key: KeyStat[];
}

interface EventItem {
  id: number;
  api_key_id: number;
  api_key_name: string;
  request_id?: string;
  method: string;
  path: string;
  status_code: number;
  latency_ms: number;
  response_bytes: number;
  ip?: string;
  user_agent?: string;
  occurred_at: string;
}

export default function DeveloperAnalytics() {
  const { workspaceId } = useParams();
  const isAdmin = useIsInAdminGroup();
  const [range, setRange] = useState<Range>('24h');
  const [summary, setSummary] = useState<Summary | null>(null);
  const [events, setEvents] = useState<EventItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(async () => {
    if (!workspaceId || !isAdmin) return;
    setLoading(true);
    setError(null);
    try {
      const [s, e] = await Promise.all([
        api.get(`/workspace/${workspaceId}/api-usage/summary`, {
          params: { range },
        }),
        api.get(`/workspace/${workspaceId}/api-usage/events`, {
          params: { limit: 25 },
        }),
      ]);
      setSummary(s.data.resource as Summary);
      setEvents((e.data.resource?.events as EventItem[]) ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load usage data');
    } finally {
      setLoading(false);
    }
  }, [workspaceId, isAdmin, range]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (!isAdmin) {
    return (
      <WorkspaceLayout>
        <Card.Root mx='auto' width={{ md: '90%' }} maxW='4xl'>
          <Card.Body>
            <Stack textAlign='center' gap={3} py={10}>
              <Heading size='lg'>Workspace admin access required</Heading>
              <Text color='fg.muted'>Only workspace admins can view API usage analytics.</Text>
            </Stack>
          </Card.Body>
        </Card.Root>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <Card.Root minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl'>
        <Card.Header>
          <Container maxW='5xl'>
            <Flex alignItems='center' mb={2} wrap='wrap' gap={2}>
              <HStack gap={3}>
                <Icon as={ChartBarIcon} w={7} h={7} color='brand.solid' />
                <Heading size='3xl'>API analytics</Heading>
              </HStack>
              <Spacer />
              <HStack gap={2}>
                <Button
                  variant='outline'
                  size='sm'
                  asChild
                  loading={false}
                >
                  <NavLink to={`/${workspaceId}/developer/keys`}>
                    <Icon as={KeyIcon} w={4} h={4} />
                    Manage keys
                  </NavLink>
                </Button>
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={() => setRefreshKey((k) => k + 1)}
                  loading={loading}
                >
                  <Icon as={ArrowPathIcon} w={4} h={4} />
                  Refresh
                </Button>
              </HStack>
            </Flex>
            <Text color='fg.muted' fontSize='sm' mb={4}>
              Per-workspace usage of the public API. Updated within ~2 seconds of each request.
              Logs reflect successfully authenticated traffic only.
            </Text>
            <HStack gap={1} flexWrap='wrap'>
              {RANGES.map((r) => (
                <Button
                  key={r.id}
                  size='xs'
                  variant={range === r.id ? 'solid' : 'outline'}
                  colorPalette={range === r.id ? 'brand' : 'gray'}
                  onClick={() => setRange(r.id)}
                  borderRadius='full'
                >
                  {r.label}
                </Button>
              ))}
            </HStack>
          </Container>
        </Card.Header>

        <Card.Body>
          <Container maxW='5xl'>
            {error && (
              <Box
                p={3}
                borderLeftWidth='3px'
                borderColor='danger'
                bg='danger.subtle'
                _dark={{ bg: 'danger.subtle' }}
                borderRadius='sm'
                mb={4}
              >
                <HStack gap={2}>
                  <Icon as={ExclamationTriangleIcon} w={4} h={4} color='danger' />
                  <Text fontSize='sm' color='danger' _dark={{ color: 'danger' }}>
                    {error}
                  </Text>
                </HStack>
              </Box>
            )}

            {loading && !summary ? (
              <Stack alignItems='center' py={16}>
                <Spinner />
                <Text color='fg.muted' fontSize='sm'>
                  Loading usage data…
                </Text>
              </Stack>
            ) : summary ? (
              <Stack gap={6}>
                <StatsRow summary={summary} />
                <CacheControlsCard workspaceId={workspaceId ?? ''} />
                <RequestsChart summary={summary} />
                <Flex gap={6} direction={{ base: 'column', lg: 'row' }}>
                  <Box flex='1' minW={0}>
                    <SectionHeading>Top endpoints</SectionHeading>
                    <EndpointTable rows={summary.by_endpoint} />
                  </Box>
                  <Box flex='1' minW={0}>
                    <SectionHeading>Usage by API key</SectionHeading>
                    <KeyTable rows={summary.by_key} />
                  </Box>
                </Flex>
                <Box>
                  <SectionHeading>Live activity</SectionHeading>
                  <EventsTable rows={events} />
                </Box>
              </Stack>
            ) : null}
          </Container>
        </Card.Body>
      </Card.Root>
    </WorkspaceLayout>
  );
}

interface CacheLayerStatus {
  last_bust_workspace?: string;
  last_bust_global?: string;
}

interface CacheStatusResp {
  public: CacheLayerStatus;
  library: CacheLayerStatus;
}

type CacheLayer = 'public' | 'library';

function CacheControlsCard({ workspaceId }: { workspaceId: string }) {
  const [status, setStatus] = useState<CacheStatusResp | null>(null);
  const [pending, setPending] = useState<CacheLayer | null>(null);
  const [lastAction, setLastAction] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    if (!workspaceId) return;
    try {
      const res = await api.get(`/workspace/${workspaceId}/cache/status`);
      setStatus(res.data.resource as CacheStatusResp);
    } catch {
      // Status is informational — silent on failure rather than blocking the
      // user from busting (they can still trigger a bust without a timestamp).
    }
  }, [workspaceId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const bust = useCallback(
    async (layer: CacheLayer) => {
      if (!workspaceId) return;
      setPending(layer);
      setActionError(null);
      try {
        const url =
          layer === 'public'
            ? `/workspace/${workspaceId}/public/cache/bust?warm=true`
            : `/workspace/${workspaceId}/library/cache/bust`;
        await api.post(url);
        setLastAction(
          layer === 'public'
            ? 'Public cache rebuilt and warmed.'
            : 'Library cache rebuilt.',
        );
        await loadStatus();
      } catch (e: unknown) {
        const err = e as { response?: { data?: { error?: string } }; message?: string };
        setActionError(err?.response?.data?.error ?? err?.message ?? 'Bust failed');
      } finally {
        setPending(null);
      }
    },
    [workspaceId, loadStatus],
  );

  return (
    <Box borderWidth='1px' borderRadius='lg' p={4} bg='bg.subtle'>
      <HStack mb={3}>
        <Icon as={CloudArrowDownIcon} w={4} h={4} color='brand.solid' />
        <SectionHeading>Cache controls</SectionHeading>
      </HStack>
      <Text fontSize='xs' color='fg.muted' mb={3}>
        Force-rebuild the Redis read cache. TTL-driven invalidation handles
        most cases automatically — use this for coordinated launches or after
        a large editorial change you don&apos;t want to wait through.
      </Text>

      <Stack gap={2}>
        <CacheRow
          title='Public API cache'
          subtitle='Customer&apos;s own workspace data (5min–24h TTLs)'
          lastBust={status?.public.last_bust_workspace}
          pending={pending === 'public'}
          actionLabel='Rebuild &amp; warm'
          onClick={() => bust('public')}
        />
        <CacheRow
          title='Library cache'
          subtitle='Base-workspace content for matching sport types (30min–24h TTLs)'
          lastBust={status?.library.last_bust_workspace}
          pending={pending === 'library'}
          actionLabel='Rebuild'
          onClick={() => bust('library')}
        />
      </Stack>

      {lastAction && (
        <HStack mt={3} gap={2} fontSize='xs' color='success'>
          <Icon as={CheckCircleIcon} w={3.5} h={3.5} />
          <Text>{lastAction}</Text>
        </HStack>
      )}
      {actionError && (
        <HStack mt={3} gap={2} fontSize='xs' color='danger'>
          <Icon as={ExclamationTriangleIcon} w={3.5} h={3.5} />
          <Text>{actionError}</Text>
        </HStack>
      )}
    </Box>
  );
}

function CacheRow({
  title,
  subtitle,
  lastBust,
  pending,
  actionLabel,
  onClick,
}: {
  title: string;
  subtitle: string;
  lastBust?: string;
  pending: boolean;
  actionLabel: string;
  onClick: () => void;
}) {
  return (
    <Flex
      gap={3}
      align='center'
      bg='bg'
      borderWidth='1px'
      borderRadius='md'
      p={3}
    >
      <Box flex='1' minW={0}>
        <Text fontSize='sm' fontWeight='semibold'>
          {title}
        </Text>
        <Text fontSize='xs' color='fg.muted'>
          {subtitle}
        </Text>
        <HStack mt={2} gap={2} color='fg.muted'>
          <Icon as={ClockIcon} w={3} h={3} />
          <Text fontSize='xs'>
            {lastBust
              ? `Rebuilt ${formatDistanceToNow(new Date(lastBust), { addSuffix: true })}`
              : 'Never manually rebuilt'}
          </Text>
        </HStack>
      </Box>
      <Button
        size='sm'
        variant='outline'
        loading={pending}
        onClick={onClick}
        flexShrink={0}
      >
        <Icon as={BoltIcon} w={3.5} h={3.5} />
        {actionLabel}
      </Button>
    </Flex>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Text
      fontSize='xs'
      fontWeight='bold'
      textTransform='uppercase'
      letterSpacing='wider'
      color='fg.muted'
      mb={2}
    >
      {children}
    </Text>
  );
}

function StatsRow({ summary }: { summary: Summary }) {
  const errorRate =
    summary.total > 0
      ? ((summary.errors_4xx + summary.errors_5xx) / summary.total) * 100
      : 0;
  return (
    <Flex gap={3} wrap='wrap'>
      <StatCard label='Total requests' value={summary.total.toLocaleString()} />
      <StatCard
        label='Success'
        value={`${summary.total > 0 ? Math.round((summary.success / summary.total) * 100) : 100}%`}
        sublabel={`${summary.success.toLocaleString()} ok`}
        tone='green'
      />
      <StatCard
        label='Client errors'
        value={summary.errors_4xx.toLocaleString()}
        sublabel='4xx'
        tone={summary.errors_4xx > 0 ? 'orange' : 'gray'}
      />
      <StatCard
        label='Server errors'
        value={summary.errors_5xx.toLocaleString()}
        sublabel='5xx'
        tone={summary.errors_5xx > 0 ? 'red' : 'gray'}
      />
      <StatCard
        label='p95 latency'
        value={`${summary.p95_ms} ms`}
        sublabel={`p50 ${summary.p50_ms} · p99 ${summary.p99_ms}`}
      />
      <StatCard
        label='Error rate'
        value={`${errorRate.toFixed(2)}%`}
        tone={errorRate > 5 ? 'red' : errorRate > 1 ? 'orange' : 'green'}
      />
    </Flex>
  );
}

function StatCard({
  label,
  value,
  sublabel,
  tone = 'gray',
}: {
  label: string;
  value: string;
  sublabel?: string;
  tone?: 'gray' | 'green' | 'orange' | 'red';
}) {
  const accent =
    tone === 'green'
      ? 'success'
      : tone === 'orange'
        ? 'warning'
        : tone === 'red'
          ? 'danger'
          : 'fg';
  return (
    <Box
      flex='1 1 160px'
      minW='160px'
      borderWidth='1px'
      borderRadius='lg'
      p={4}
      bg='bg.subtle'
    >
      <Text
        fontSize='label'
        textTransform='uppercase'
        letterSpacing='wider'
        color='fg.muted'
        fontWeight='bold'
        mb={1}
      >
        {label}
      </Text>
      <Heading size='xl' color={accent} lineHeight='1.1'>
        {value}
      </Heading>
      {sublabel && (
        <Text fontSize='xs' color='fg.muted' mt={1}>
          {sublabel}
        </Text>
      )}
    </Box>
  );
}

function RequestsChart({ summary }: { summary: Summary }) {
  const { labels, totals, errors } = useMemo(() => {
    const fmt = summary.bucket === 'hour' ? 'MMM d, HH:mm' : 'MMM d';
    return {
      labels: summary.timeseries.map((b) => format(new Date(b.bucket), fmt)),
      totals: summary.timeseries.map((b) => b.total),
      errors: summary.timeseries.map((b) => b.errors),
    };
  }, [summary]);

  if (summary.timeseries.length === 0) {
    return (
      <Box
        borderWidth='1px'
        borderRadius='lg'
        p={8}
        textAlign='center'
        bg='bg.subtle'
      >
        <Text color='fg.muted' fontSize='sm'>
          No traffic in this range yet. Make a request from the docs or your integration to see
          it show up here.
        </Text>
      </Box>
    );
  }

  const data = {
    labels,
    datasets: [
      {
        label: 'Requests',
        data: totals,
        borderColor: '#3182ce',
        backgroundColor: 'rgba(49, 130, 206, 0.12)',
        borderWidth: 2,
        fill: true,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
      {
        label: 'Errors',
        data: errors,
        borderColor: '#e53e3e',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        borderDash: [4, 4],
        fill: false,
        tension: 0.3,
        pointRadius: 0,
        pointHoverRadius: 4,
      },
    ],
  };

  return (
    <Box borderWidth='1px' borderRadius='lg' p={4} bg='bg.subtle'>
      <HStack mb={2} gap={4}>
        <SectionHeading>Requests over time</SectionHeading>
        <Spacer />
        <HStack gap={3} fontSize='xs' color='fg.muted'>
          <HStack gap={1}>
            <Box w='10px' h='10px' borderRadius='sm' bg='#3182ce' />
            <Text>Requests</Text>
          </HStack>
          <HStack gap={1}>
            <Box
              w='10px'
              h='2px'
              borderTopWidth='2px'
              borderColor='#e53e3e'
              borderStyle='dashed'
            />
            <Text>Errors</Text>
          </HStack>
        </HStack>
      </HStack>
      <Box height='220px'>
        <Line
          data={data}
          options={{
            maintainAspectRatio: false,
            responsive: true,
            interaction: { mode: 'index', intersect: false },
            plugins: { legend: { display: false }, tooltip: { intersect: false } },
            scales: {
              x: { grid: { display: false } },
              y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.05)' } },
            },
          }}
        />
      </Box>
    </Box>
  );
}

function EndpointTable({ rows }: { rows: EndpointStat[] }) {
  if (rows.length === 0) {
    return <EmptyHint>No endpoints accessed in this range.</EmptyHint>;
  }
  return (
    <Box borderWidth='1px' borderRadius='lg' overflowX='auto'>
      <Table.Root size='sm' variant='line'>
        <Table.Header>
          <Table.Row bg='bg.subtle'>
            <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Requests</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Errors</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>p95</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((r) => (
            <Table.Row key={`${r.method}-${r.path}`}>
              <Table.Cell>
                <HStack gap={2}>
                  <Badge size='xs' variant='success' fontFamily='mono'>
                    {r.method}
                  </Badge>
                  <Text fontFamily='mono' fontSize='xs' truncate>
                    {r.path}
                  </Text>
                </HStack>
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {r.total.toLocaleString()}
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {r.errors > 0 ? (
                  <Text as='span' color='danger'>
                    {r.errors}
                  </Text>
                ) : (
                  <Text as='span' color='fg.muted'>
                    0
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {r.p95_ms} ms
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

function KeyTable({ rows }: { rows: KeyStat[] }) {
  if (rows.length === 0) {
    return <EmptyHint>No API key activity in this range.</EmptyHint>;
  }
  return (
    <Box borderWidth='1px' borderRadius='lg' overflowX='auto'>
      <Table.Root size='sm' variant='line'>
        <Table.Header>
          <Table.Row bg='bg.subtle'>
            <Table.ColumnHeader>Key</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Requests</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Errors</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Last seen</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((r) => (
            <Table.Row key={r.api_key_id}>
              <Table.Cell>
                <HStack gap={2}>
                  <Icon as={KeyIcon} w={3.5} h={3.5} color='fg.muted' />
                  <Text fontSize='sm' truncate maxW='220px'>
                    {r.name}
                  </Text>
                </HStack>
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {r.total.toLocaleString()}
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {r.errors > 0 ? (
                  <Text as='span' color='danger'>
                    {r.errors}
                  </Text>
                ) : (
                  <Text as='span' color='fg.muted'>
                    0
                  </Text>
                )}
              </Table.Cell>
              <Table.Cell textAlign='end' fontSize='xs' color='fg.muted'>
                {formatDistanceToNow(new Date(r.last_seen), { addSuffix: true })}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

function EventsTable({ rows }: { rows: EventItem[] }) {
  if (rows.length === 0) {
    return <EmptyHint>No requests recorded yet.</EmptyHint>;
  }
  return (
    <Box borderWidth='1px' borderRadius='lg' overflowX='auto'>
      <Table.Root size='sm' variant='line'>
        <Table.Header>
          <Table.Row bg='bg.subtle'>
            <Table.ColumnHeader>When</Table.ColumnHeader>
            <Table.ColumnHeader>Endpoint</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='center'>Status</Table.ColumnHeader>
            <Table.ColumnHeader textAlign='end'>Latency</Table.ColumnHeader>
            <Table.ColumnHeader>Key</Table.ColumnHeader>
            <Table.ColumnHeader>IP</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {rows.map((e) => (
            <Table.Row key={e.id}>
              <Table.Cell whiteSpace='nowrap'>
                <HStack gap={2} color='fg.muted'>
                  <Icon as={ClockIcon} w={3} h={3} />
                  <Text fontSize='xs'>
                    {formatDistanceToNow(new Date(e.occurred_at), { addSuffix: true })}
                  </Text>
                </HStack>
              </Table.Cell>
              <Table.Cell>
                <HStack gap={2}>
                  <Badge size='xs' variant='success' fontFamily='mono'>
                    {e.method}
                  </Badge>
                  <Text fontFamily='mono' fontSize='xs' truncate>
                    {e.path}
                  </Text>
                </HStack>
              </Table.Cell>
              <Table.Cell textAlign='center'>
                <Badge
                  size='xs'
                  variant={statusPalette(e.status_code)}
                  fontFamily='mono'
                >
                  {e.status_code}
                </Badge>
              </Table.Cell>
              <Table.Cell textAlign='end' fontFamily='mono' fontSize='xs'>
                {e.latency_ms} ms
              </Table.Cell>
              <Table.Cell>
                <Text fontSize='xs' truncate maxW='160px'>
                  {e.api_key_name}
                </Text>
              </Table.Cell>
              <Table.Cell>
                <Text fontFamily='mono' fontSize='xs' color='fg.muted'>
                  {e.ip ?? '—'}
                </Text>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

function statusPalette(status: number): 'success' | 'warning' | 'danger' | undefined {
  if (status >= 200 && status < 300) return 'success';
  if (status >= 300 && status < 400) return 'warning';
  if (status >= 400 && status < 500) return 'warning';
  if (status >= 500) return 'danger';
  return undefined;
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <Box borderWidth='1px' borderRadius='lg' p={6} bg='bg.subtle' textAlign='center'>
      <Text color='fg.muted' fontSize='sm'>
        {children}
      </Text>
    </Box>
  );
}
